import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Grid2, Typography } from '@mui/material';
import { useLoadScript } from '@react-google-maps/api';
import { GOOGLE_MAP_API_KEY } from '../../api';
import { useNavigate } from 'react-router';
import { ERoutes } from '../../enums';
import ToggleViewButtons from '../../components/Game/ToggleViewButtons/ToggleViewButtons';
import { getGeneralModeData, postGeneralResult } from '../../queries';
import { materialStyles } from './styles';
import { useGeneralMode } from './useGeneralMode';
import GoogleMapComponent from '../../components/Game/GoogleMapComponent/GoogleMapComponent';
import GeneralModals from '../../components/Game/GeneralModals/GeneralModals';
import { useTimer } from '../../hooks/useTimer';
import SplashScreen from '../SplashScreen/SplashScreen';
import { ScreenWrapper } from '../../components/Wrappers/ScreenWrapper';
import { useTranslation } from 'react-i18next';
import AttemptsWidget from '../../components/AttemptsWidget/AttemptsWidget';
import PieTimer from '../../components/PieTimer/PieTimer';
import { Icon } from '../../components/Icon';
import i18n from '../../../assets/i18n';

const GeneralModeScreen: React.FC = () => {
  const [coordinates, setCoordinates] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const {
    latitude,
    longitude,
    attempts,
    score,
    disableTries,
    deadline,
    totalAttempts,
  } = useGeneralMode();

  const [isTimerDisabled, setIsTimerDisabled] = useState(false);
  const [showFailedGameModal, setShowFailedGameModal] = useState(false);

  const timer = useTimer(deadline, isTimerDisabled, 'SECONDS');

  useEffect(() => {
    if (deadline && timer === null) {
      setShowFailedGameModal(true);
      setIsTimerDisabled(true);
    }
  }, [timer, deadline]);

  const navigate = useNavigate();
  const [isStreetView, setIsStreetView] = useState<boolean>(true);

  const [showResultModal, setShowResultModal] = useState(disableTries);
  const [showRulesModal, setShowRulesModal] = useState(false);

  const handleResultModalOpen = useCallback(() => setShowResultModal(true), []);

  const handleResultModalClose = useCallback(
    () => setShowResultModal(false),
    [],
  );

  const isDestinationLoaded = useMemo(
    () => Boolean(latitude) && Boolean(longitude),
    [latitude, longitude],
  );

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAP_API_KEY,
    language: i18n.language,
  });

  const handleBack = useCallback(() => {
    setShowFailedGameModal(false);
    navigate(ERoutes.gameModes);
  }, [navigate, setShowFailedGameModal]);

  const setActiveView = useCallback((value: boolean): void => {
    setIsStreetView(value);
  }, []);

  // eslint-disable-next-line no-undef
  const onMapClick = useCallback((event: google.maps.MapMouseEvent) => {
    if (!event.latLng) return;
    setCoordinates({
      lat: event.latLng.lat(),
      lng: event.latLng.lng(),
    });
  }, []);

  const onSubmit = useCallback(async () => {
    if (!coordinates) {
      return;
    }

    try {
      setIsTimerDisabled(true);
      await postGeneralResult(coordinates.lat, coordinates.lng);
      handleResultModalOpen();
    } catch (error) {
      console.error('Error while [postGeneralResult]', error);
    }
  }, [coordinates, handleResultModalOpen]);

  const showRules = useCallback(() => {
    setShowRulesModal(true);
  }, [setShowRulesModal]);

  const { t } = useTranslation();

  if (!isLoaded) return <SplashScreen />;

  return (
    <ScreenWrapper onBack={handleBack} isAbsolute>
      <Grid2 sx={materialStyles.container}>
        <Grid2 sx={materialStyles.timerContainer}>
          <PieTimer timer={timer} isTimerDisabled={isTimerDisabled} />
        </Grid2>

        <Grid2 sx={materialStyles.topButtonsWrapper}>
          {attempts != null && totalAttempts && (
            <Grid2 sx={materialStyles.attemptsContainer}>
              <AttemptsWidget
                attempts={attempts}
                totalAttempts={totalAttempts}
              />
            </Grid2>
          )}

          <Button sx={materialStyles.rulesBtn} onClick={showRules}>
            <Icon sx={materialStyles.rulesIcon} iconName="RulesIcon" />
            <Grid2 sx={materialStyles.rulesIconTextWrapper}>
              <Typography sx={materialStyles.rulesIconText}>
                {t('game.rules')}
              </Typography>
            </Grid2>
          </Button>
        </Grid2>

        <GoogleMapComponent
          isStreetView={isStreetView}
          isDestinationLoaded={isDestinationLoaded}
          coordinates={coordinates}
          onMapClick={onMapClick}
          latitude={latitude}
          longitude={longitude}
        />

        <Grid2 sx={materialStyles.toggleViewButtons}>
          <ToggleViewButtons
            setActiveView={setActiveView}
            isStreetView={isStreetView}
            onSubmit={onSubmit}
            coordinates={coordinates}
          />
        </Grid2>
      </Grid2>

      <GeneralModals
        showResultModal={showResultModal}
        handleResultModalClose={handleResultModalClose}
        setIsStreetView={setIsStreetView}
        disableTries={disableTries}
        score={score}
        attempts={attempts}
        setIsTimerDisabled={setIsTimerDisabled}
        showFailedGameModal={showFailedGameModal}
        setShowFailedGameModal={setShowFailedGameModal}
        fetchDataFunction={getGeneralModeData}
        showRulesModal={showRulesModal}
        setShowRulesModal={setShowRulesModal}
      />
    </ScreenWrapper>
  );
};

export default GeneralModeScreen;
